import { SyntheticEvent } from 'react';
import moment from 'moment';
import errorImage from '@assets/default_cover.svg';
import AppConstants, { isProductionHost } from '../common/AppConstants';

const getHostNameFromUrl = () => window.location.hostname || '';

/**
 * General utility functions
 */
const Util = {

  /**
   Checking for Env's since every env has different endpoint
   */
  isLocal(): boolean {
    return ['localhost'].includes(window.location.hostname);
  },
  isQAEnv(): boolean {
    return ['qa.boomi.com'].includes(window.location.hostname);
  },
  isPRODEnv(): boolean {
    return ['platform.boomi.com'].includes(window.location.hostname);
  },
  isBaiQAEnv(): boolean {
    return ['baiqa1.sandbox.boomi.com'].includes(window.location.hostname);
  },
  isProdCloneEnv(): boolean {
    return ['prodclone.boomi.com'].includes(window.location.hostname);
  },
  isBiAIAgentEnv(): boolean {
    return ['biaiagentp.datalake-sandbox.boomi.com'].includes(window.location.hostname);
  },
  isProdClone4Env(): boolean {
    return ['pc4.preprod.boomi.com'].includes(window.location.hostname);
  },
  isPopeyeEnv(): boolean {
    return ['popeye1.sandbox.boomi.com'].includes(window.location.hostname);
  },
  isEuQaEnv(): boolean {
    return ['euqapl.eu-pipeline.boomi.com'].includes(window.location.hostname);
  },
  isEuPRODEnv(): boolean {
    return ['platform.gb.boomi.com'].includes(window.location.hostname);
  },

  /**
   * Read cookie by name
   * @param name
   */
  getCookie(name: string): string | null {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        .map((cookie) => decodeURIComponent(cookie.substring(nameLenPlus)))[0] || null
    );
  },

  /**
   * Function to set cookies. This is used in Dev mode only
   * @param name
   * @param val
   */
  setCookie(name: string, val: string | null) {
    const value = val;
    document.cookie = `${name}=${value}; path=/; secure;`;
  },

  /**
   * Is the app running in development mode
   */
  isDevelopmentMode(): boolean {
    return !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
  },

  /**
   * Function to get backend host url.
   * This is based on environment variable if the app is
   * running in development mode,.ie, 'npm start'
   * If not running in dev mode, it returns the current host url based on window.location
   */
  getBackendHost(): string | undefined {
    return this.isDevelopmentMode()
      ? process.env.REACT_APP_DEV_PROXY_BACKEND
      : this.getCurrentHost();
  },

  /**
   * Function to get current host url
   */
  getCurrentHost(): string {
    return `${window.location.protocol}//${window.location.host}`;
  },

  /**
   * Function to prepend backend host to relative image URLs
   * Returns absolute URL for images from marketplace/discover
   * @param relativeUrl - Relative image path from API (e.g., "marketplace/...")
   * @returns Absolute URL with backend host prepended
   */
  getImageUrl(relativeUrl: string | null | undefined): string {
    const MARKETPLACE_MEDIA_FILE_BASE_SANDBOX = 'https://scratch.marketplace.cj-sandbox.boomi.com';
    const MARKETPLACE_MEDIA_FILE_BASE_QA = 'https://qa.marketplace.cj-pipeline.boomi.com';
    const MARKETPLACE_MEDIA_FILE_BASE_PRODCLONE = 'https://prodclone.marketplace.cj-prod.boomi.com';
    const MARKETPLACE_MEDIA_FILE_BASE_PRODCLONE4 = 'https://prodclone4.marketplace.cj-preprod.boomi.com';
    const MARKETPLACE_MEDIA_FILE_BASE_PROD = 'https://marketplace.boomi.com';

    if (!relativeUrl) return '';

    // If URL is already absolute (starts with http:// or https://), return as is
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
      return relativeUrl;
    }

    const baseUrl = () => {
      if (this.isLocal() || this.isBiAIAgentEnv() || this.isPopeyeEnv()) {
        return MARKETPLACE_MEDIA_FILE_BASE_SANDBOX;
      }
      if (this.isQAEnv()) {
        return MARKETPLACE_MEDIA_FILE_BASE_QA;
      }
      if (this.isProdCloneEnv()) {
        return MARKETPLACE_MEDIA_FILE_BASE_PRODCLONE;
      }
      if (this.isProdClone4Env()) {
        return MARKETPLACE_MEDIA_FILE_BASE_PRODCLONE4;
      }
      return MARKETPLACE_MEDIA_FILE_BASE_PROD;
    };

    // Ensure no double slashes when joining
    const cleanPath = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;

    return `${baseUrl()}${cleanPath}`;
  },

  /**
   * Function to render default image when actual image is not rendered
   */
  getDefaultImage(e: SyntheticEvent<HTMLImageElement, Event>) {
    e.currentTarget.src = errorImage;
  },

  /**
   * function to return month in MMM format
   */
  getMonth(month: number) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return months[month];
  },

  /**
   * function to return day difference from today
   * 0 -> today
   * 1 -> yesterday
   * 2 -> ...
   */
  getDiffInDays(dateTimeStamp: any) {
    return moment(Date.now()).diff(moment(dateTimeStamp).startOf('day'), 'days');
  },

  getFullMonth(month: number) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    return months[month];
  },

  /**
   * function to return application environment
   */
  getAppEnvironment(): string {
    const { hostname } = window.location;
    const appEnvironment = hostname.split('.')[0] ?? hostname;
    return appEnvironment;
  },

  delay(delay: number) {
    const delayInMS = delay || 0;
    return new Promise((done: any) => {
      setTimeout(() => {
        done();
      }, delayInMS);
    });
  },

  /**
   * Function to check if the url has any query param with the provide name.
   * It can be present in the hash part as well.
   * @param queryParam
   */
  checkIfUrlHasQueryParam(queryParam: string) {
    let hasParam = false;
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      hasParam = urlParams.has(queryParam);
    } else if (window.location.hash) {
      hasParam = window.location.hash.includes(queryParam);
    }

    return hasParam;
  },

  navigateToPageUrl(url: string) {
    window.location.href = url;
  },

  getSolutionsGraphQLEndpoint(): string | undefined {
    if (isProductionHost()) {
      return AppConstants.MARKETPLACE_AGENTS_GRAPHQL_ENDPOINT;
    }
    if (
      getHostNameFromUrl().includes(AppConstants.QA_HOST)
        || getHostNameFromUrl().includes('prodclone')
        || getHostNameFromUrl().includes('preprod')
    ) {
      return AppConstants.MARKETPLACE_AGENTS_GRAPHQL_ENDPOINT;
    }

    // For all other dev env
    return AppConstants.MARKETPLACE_AGENTS_GRAPHQL_ENDPOINT;
  },

  getBundleDetailsEndpoint(bundleId: string): string {
    const host = this.getBackendHost() || '';
    const accountId = this.getCookie(AppConstants.ACCOUNT_ID_COOKIE_NAME);

    return `${host}/bundle-service/v1/bundles/${bundleId}/artifacts?accountId=${accountId}`;
  },

  getDownloadSupportingMaterialEndpoint(bundleId: string, artifactId: string): string {
    const host = this.getBackendHost() || '';
    const accountId = this.getCookie(AppConstants.ACCOUNT_ID_COOKIE_NAME);

    return `${host}/bundle-service/v1/bundles/${bundleId}/artifacts/${artifactId}?accountId=${accountId}`;
  },

  getDownloadCompressedSupportingMaterialEndpoint(bundleId: string): string {
    const host = this.getBackendHost() || '';
    const accountId = this.getCookie(AppConstants.ACCOUNT_ID_COOKIE_NAME);

    return `${host}/bundle-service/v1/bundles/${bundleId}/artifacts/download?accountId=${accountId}`;
  },

  getSolutionInstallEndpoint(): string {
    const host = this.getBackendHost() || '';
    const accountId = this.getCookie(AppConstants.ACCOUNT_ID_COOKIE_NAME);

    return `${host}/bundle-service/v1/bundles/installations?accountId=${accountId}`;
  },

  getMarketplaceMetricsEndpoint(): string {
    const host = this.getBackendHost() || '';
    const accountId = this.getCookie(AppConstants.ACCOUNT_ID_COOKIE_NAME);

    return `${host}/bundle-service/api/marketplace/metrics?accountId=${accountId}`;
  },

  getPlatformBaseURL(): string | undefined {
    return this.isDevelopmentMode()
      ? process.env.REACT_APP_NEXT_PUBLIC_PLATFORM_BASE_URL
      : this.getCurrentHost();
  },

  getPlatformAtomSphereBaseURL() {
    return `${this.getPlatformBaseURL()}/AtomSphere.html?`;
  },

  isValidToken(): boolean {
    let flag = false;
    const tokenId = this.getCookie('TOKEN_ID');
    const tokenSecret = this.getCookie('TOKEN_SECRET');
    flag = !!(tokenId && tokenId !== 'null' && tokenId.length > 0
      && tokenSecret && tokenSecret !== 'null' && tokenSecret.length > 0);
    return flag;
  },
};

export default Util;
